import React, { useEffect, useState, useRef } from "react";
import { View, Text } from "react-native";
import { AppColors } from "../../../../../utility/AppColors";
import { AppFontStyle } from "../../../../../styles/AppFontStyle";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";

interface EventNameSectionProps {
  eventName?: string;
  eventStartTime?: string;
  transparent?: boolean; // For when embedded in unified header
  onTimerEnd?: () => void; // Callback when timer reaches 0
}

const EventNameSection: React.FC<EventNameSectionProps> = ({
  eventName,
  eventStartTime,
  transparent = false,
  onTimerEnd,
}) => {
  const deviceSize = useDeviceSize();
  const isMobile =
    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [showTimer, setShowTimer] = useState(false);
  const hasCalledOnTimerEnd = useRef(false);

  // Use props directly
  const eventTitle = eventName || "[Event Name]";
  const startTime = eventStartTime;

  useEffect(() => {
    if (!startTime) return;

    const checkTimer = () => {
      const now = new Date().getTime();
      const eventTime = new Date(startTime).getTime();
      const difference = eventTime - now;

      if (difference > 0) {
        setShowTimer(true);
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60)
        );
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ hours, minutes, seconds });
      } else {
        setShowTimer(false);
        // Call onTimerEnd callback only once when timer ends
        if (!hasCalledOnTimerEnd.current && onTimerEnd) {
          hasCalledOnTimerEnd.current = true;
          onTimerEnd();
        }
      }
    };

    checkTimer();
    const interval = setInterval(checkTimer, 1000);

    return () => clearInterval(interval);
  }, [startTime, onTimerEnd]);

  const formatTime = (time: number) => time.toString().padStart(2, "0");

  // Hide entire section on mobile when timer is not active
  if (isMobile && !showTimer) {
    return null;
  }

  return (
    <View
      style={{
        backgroundColor: transparent ? "transparent" : "#1e293b", // Conditional background
        paddingVertical: transparent ? 0 : isMobile ? 12 : 16,
        paddingHorizontal: transparent ? 0 : 15,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {/* Event Name */}
      <Text
        style={[
          AppFontStyle.BOLD_ANYBODY_18,
          {
            color: "white",
            fontSize: isMobile ? 16 : 18,
            flex: 1,
          },
        ]}
        numberOfLines={1}
      >
        {eventTitle}
      </Text>

      {/* Timer */}
      {showTimer && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: isMobile ? 4 : 6,
            marginRight: isMobile ? 0 : 20,
          }}
        >
          {/* Timer Label */}
          <Text
            style={[
              AppFontStyle.REGULAR_ANYBODY_14,
              {
                color: "white",
                fontSize: isMobile ? 12 : 14,
                marginRight: isMobile ? 6 : 8,
              },
            ]}
          >
            Event will begin in:
          </Text>
          {/* Hours */}
          <View
            style={{
              backgroundColor: AppColors.koBrandRed,
              borderRadius: 8,
              paddingHorizontal: isMobile ? 8 : 12,
              paddingVertical: isMobile ? 6 : 8,
              minWidth: isMobile ? 40 : 50,
              alignItems: "center",
            }}
          >
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_16,
                {
                  color: "white",
                  fontSize: isMobile ? 14 : 16,
                },
              ]}
            >
              {formatTime(timeLeft.hours)}
            </Text>
          </View>

          {/* Colon separator */}
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_16,
              {
                color: "white",
                fontSize: isMobile ? 14 : 16,
                paddingHorizontal: 2,
              },
            ]}
          >
            :
          </Text>

          {/* Minutes */}
          <View
            style={{
              backgroundColor: AppColors.koBrandRed,
              borderRadius: 8,
              paddingHorizontal: isMobile ? 8 : 12,
              paddingVertical: isMobile ? 6 : 8,
              minWidth: isMobile ? 40 : 50,
              alignItems: "center",
            }}
          >
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_16,
                {
                  color: "white",
                  fontSize: isMobile ? 14 : 16,
                },
              ]}
            >
              {formatTime(timeLeft.minutes)}
            </Text>
          </View>

          {/* Colon separator */}
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_16,
              {
                color: "white",
                fontSize: isMobile ? 14 : 16,
                paddingHorizontal: 2,
              },
            ]}
          >
            :
          </Text>

          {/* Seconds */}
          <View
            style={{
              backgroundColor: AppColors.koBrandRed,
              borderRadius: 8,
              paddingHorizontal: isMobile ? 8 : 12,
              paddingVertical: isMobile ? 6 : 8,
              minWidth: isMobile ? 40 : 50,
              alignItems: "center",
            }}
          >
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_16,
                {
                  color: "white",
                  fontSize: isMobile ? 14 : 16,
                },
              ]}
            >
              {formatTime(timeLeft.seconds)}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

export default EventNameSection;
